<template>
	<div v-if="!postObject.deleted" :data-weight="postObject.popular_weight" class="min-w-0">
		<div class="card p-0 rounded-none md:rounded-xl"
			 :class="{
				'card-white': !nested,
				'border-2 border-orange' : postObject.is_pinned && !view && !nested,
				'group': !view,
			}"
		>
			<div class="p-4 max-w-2xl mx-auto pb-3"
				 :class="{'md:p-6': !nested}"
			>
				<div class="flex items-start space-x-2 justify-between max-w-full leading-0">
					<div class="flex items-center space-x-2 flex-1 truncate">
						<a href="/best" v-if="postObject.is_best && !nested" title="Лучшее из лучших">
							<svg-icon icon="star" set="local" class="w-4 h-4 text-orange"></svg-icon>
						</a>

						<svg-icon v-if="postObject.pinned && !nested"
								  title="Пост закреплен"
								  icon="pin-angle-fill"
								  class="w-4 h-4 text-orange"
						></svg-icon>

						<div v-if="muted">
							Пост скрыт
						</div>
						<div v-if="!muted"
							 class="px-0.5 flex space-x-4 max-w-full truncate whitespace-nowrap items-center">

							<span v-if="!postObject.is_published" class="badge badge-red badge-xs">Черновик</span>

							<channel-link
								v-if="postObject.channel"
								:channel="postObject.channel"
								:user="postObject.author"
							></channel-link>
							<user-link
								v-else
								:user="postObject.author"
							></user-link>
						</div>
					</div>
					<div v-if="!nested" class="flex space-x-3 items-center max-w-full">
						<svg-icon
							v-if="postObject.is_public"
							icon="globe"
							class="text-orange w-3 h-3" title="Публичный пост"
						></svg-icon>
						<a v-if="postObject.date" :href="postObject.url">
							<smartdate
								:date="postObject.date"
								future-prefix=""
								class="text-gray-400 text-xs"
							></smartdate>
						</a>
						<countdown
							title="Пост будет удален автоматически"
							v-if="view && postObject.deleted_after_seconds !== null"
							:seconds="postObject.deleted_after_seconds"
							class="text-xs"
							@expired="expirePost"
						></countdown>
						<div class="relative w-5 h-5 shrink-0">
							<post-options
								v-if="user.check()"
								:post="post"
								@muted="muted = true"
								@unmuted="muted = false"
								:view="view"
								@postUpdated="updatePost"
								@postDeleted="deletePost"
								@postUnpublished="unpublishPost"
							></post-options>
						</div>
					</div>
				</div>

				<div v-if="!muted && !isExpired" class="relative"
					 :class="{'mt-4': !nested, 'mt-2': nested}"
				>
					<div class="leading-tight">

						<h1 v-if="view && postObject.header"
							class="block pt-2 text-black dark:text-white text-3xl font-medium mb-4 break-words"
						>
							{{ postObject.header }}
						</h1>

						<a v-if="!view && postObject.header" :href="postObject.url"
						   class="block text-black dark:text-white font-medium mb-2 break-words w-full max-w-full"
						   :class="{'text-2xl': !nested, 'text-lg leading-tight': nested}"
						>
							{{ postObject.header }}
						</a>
					</div>

					<post-body
						:postid="postObject.id"
						:blocks="view ? postObject.blocks : postObject.preview"
						:post="postObject"
						:has-more="!view && postObject.read_more"
						:url="postObject.url"
						:view="view"
						:compact="nested"
						class="break-words w-full max-w-full overflow-hidden"
						:class="{
                            'text-sm': nested,
						}"
					></post-body>

					<a v-if="!view && (!postObject.preview_has_media || nested)"
					   :href="postObject.url"
					   class="absolute inset-0 z-0"></a>
				</div>

				<div v-if="postObject.parent"
				   class="flex mt-4 items-start leading-tight border border-gray-200 rounded-lg hover:bg-gray-50 duration-100 transition-colors dark:border-slate-700 dark:hover:bg-slate-700/50">
					<svg-icon icon="reply-fill" class="text-purple-500 w-6 h-6 m-3 mr-0"></svg-icon>
					<post-item class="flex-1" :post="postObject.parent" :nested="1"></post-item>
				</div>

			</div>

			<div v-if="!nested"
				 class="backdrop-blur-2xl bg-white/80 dark:bg-slate-800/80 rounded-b-xl"
				 :class="{'sticky bottom-12 md:bottom-0 pb-safe': view}"
			>
				<div v-if="user.check() && !muted && postObject.is_published" class="max-w-2xl mx-auto p-4 md:px-6">
					<div class="flex space-x-2 justify-between text-gray-500 text-sm lg:text-base">
						<div class="flex space-x-6 items-center leading-none">
							<a :href="postObject.url + '#comments'"
							   class="flex space-x-2 items-center text-gray-500 hover:text-black dark:hover:text-slate-400">
								<svg-icon icon="chat" class="w-4 h-4"></svg-icon>
								<div v-if="postObject.comments_count">
									{{ postObject.comments_count }}
								</div>
							</a>

							<a :href="'/new?parent=' + postObject.id" target="_blank"
							   class="text-gray-500 hover:text-black dark:hover:text-slate-400" title="ответить постом">
								<svg-icon icon="reply" class="w-5 h-5"></svg-icon>
							</a>

							<favorite-toggle type="post" :id="postObject.id"
											 :value="postObject.favorite"></favorite-toggle>

							<copy-link :url="post.url"
									   class="cursor-pointer w-5 h-5 text-gray-500 hover:text-blue-500"
							></copy-link>

							<div v-if="user.check() && postObject.views"
								 class="text-gray-500/70 dark:text-white/50 flex space-x-2 items-center">
								<svg-icon icon="eye" class="w-4 h-4"></svg-icon>
								<div class="text-sm">{{ postObject.views }}</div>
							</div>
						</div>

						<rate
							v-if="!postObject.disable_rating && !(postObject.channel && postObject.channel.disable_rating)"
							type="post"
							:id="postObject.id"
							:voted="postObject.voted"
							:can-vote="user.check"
							:rate="postObject.rate"
						></rate>
					</div>
				</div>
			</div>

			<div
				v-if="!nested && !postObject.republish_status && user.check() && !postObject.is_published && postObject.can_publish"
				class="p-6 max-w-2xl mx-auto">
				<button
					@click="publishWithConfirmation"
					:disabled="loading"
					class="btn btn-success btn-sm cursor-pointer" v-html="publishLabel"></button>
				<confirm-dialog ref="confirmDialog"></confirm-dialog>
			</div>

			<div v-if="view && !nested"
				 class="flex min-w-0 items-start space-x-4 md:justify-between p-6 py-4 max-w-2xl mx-auto">
				<user-link class="flex-1 min-w-0" :user="postObject.author" :extended="1" :show-rating="1"></user-link>
				<subscribe-button-compact
					v-if="user.check() && user.id() !== postObject.user_id"
					:compact="1"
					type="user"
					:id="postObject.author.id"
					:subscribed="postObject.author.subscribed"></subscribe-button-compact>
			</div>
		</div>
	</div>
</template>
<script>

import Client from "../../js/modules/Client";
import {notify} from "@kyvg/vue3-notification";
import ConfirmDialog from "../partials/ConfirmDialog.vue";
import User from "../../js/modules/User";
import UserLink from "../user/UserLink.vue";
import ChannelLink from "../partials/ChannelLink.vue";
import CopyLink from "../partials/CopyLink.vue";
import Countdown from "../components/Countdown.vue";
import SubscribeButtonCompact from "../partials/SubscribeButtonCompact.vue";

export default {
	components: {SubscribeButtonCompact, Countdown, CopyLink, ChannelLink, UserLink, ConfirmDialog},
	props: [
		'post',
		'view',
		'nested',
	],
	data() {
		return {
			postObject: this.post,
			muted: false,
			hidden: false,
			loading: false,
			user: new User(),
		}
	},
	watch: {
		'$props': {
			handler: function (val) {
				this.postObject = val.post;
			},
			deep: true
		}
	},
	methods: {
		updatePost(updatedPost) {
			this.postObject = updatedPost;
		},
		deletePost() {
			this.postObject.deleted = true;
			notify({
				text: 'Пост удален',
				type: 'success',
			});
		},
		unpublishPost() {
			this.postObject.is_published = false;
			notify({
				type: 'success',
				text: 'Пост распубликован и отправлен в черновики',
			});
		},
		inlineBg(ava) {
			if (!ava) {
				return '';
			}
			return {backgroundImage: `url(${ava})`};
		},
		expirePost() {
			this.postObject.deleted = true;
		},
		plural(count, words) {
			let cases = [2, 0, 1, 1, 1, 2];
			return count + ' ' + words[(count % 100 > 4 && count % 100 < 20) ? 2 : cases[Math.min(count % 10, 5)]];
		},
		async publishWithConfirmation() {
			const ok = await this.$refs.confirmDialog.show({
				title: 'Опубликовать статью?',
				message: 'Она станет видна всем пользователям',
				okButton: 'Опубликовать',
			})

			if (!ok) {
				return;
			}

			this.loading = true;

			Client
				.post('/api/v1.1/posts/' + this.postObject.id + '/publish')
				.then(response => {
					let data = response.data.data;

					notify({
						type: 'success',
						text: 'Пост опубликован',
					});

					window.location.href = data.url;
				})
				.catch(error => {
					notify({
						type: 'error',
						text: error.response.data.message || 'Какая-то ошибка, передай Зоибане пусть чинит',
					})
				})
				.finally(() => {
					this.loading = false;
				});
		}
	},
	computed: {
		publishLabel() {
			if (this.loading) {
				return 'Падажжи';
			}

			return 'Опубликовать';
		},
		isExpired() {
			return this.postObject.deleted_after_seconds !== null && this.postObject.deleted_after_seconds <= 0;
		},
	}
}
</script>
